import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography } from '../typography';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollEnd } from '@/hooks/useScrollEnd';
import { useDragToPan } from '@/hooks/useDragToPan';
import { Icon } from '../icon';
import Icons from '@/const/icons';
import Link from 'next/link';

const IMAGE_EXTENSIONS = [
	'.jpg',
	'.jpeg',
	'.png',
	'.gif',
	'.webp',
	'.bmp',
	'.svg',
];

const isImageUrl = (url: string): boolean => {
	if (!url) return false;
	const cleanUrl = url.split('?')[0].toLowerCase();
	return IMAGE_EXTENSIONS.some((ext) => cleanUrl.endsWith(ext));
};

export interface ZoomControlsProps {
	zoomOut: () => void;
	zoomIn: () => void;
	scale: number;
	handleZoomChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	pdfUrl: string;
	inline?: boolean;
}

export const ZoomControls = ({
	zoomOut,
	zoomIn,
	scale,
	handleZoomChange,
	pdfUrl,
	inline = false,
}: ZoomControlsProps) => (
	<div
		className={
			inline
				? 'flex items-center gap-2'
				: 'absolute bottom-3 left-1/2 -translate-x-1/2 z-10 hidden lg:flex items-center gap-4'
		}
	>
		<div
			className={
				inline
					? 'flex items-center gap-2 bg-gray-5 rounded-3xl max-h-10 max-w-[192px] p-2 shadow-shadow2 border border-[#0D061614]'
					: 'flex items-center gap-2 bg-gray-5 rounded-3xl max-h-10 max-w-[192px] p-2 shadow-shadow2 border border-[#0D061614]'
			}
		>
			<button
				type="button"
				onClick={zoomOut}
				className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
			>
				<Icon src={Icons.searchZoomOut} width={25} height={25} />
			</button>
			<input
				type="range"
				min={0.5}
				max={2.5}
				step={0.1}
				value={scale}
				onChange={handleZoomChange}
				className="zoom-range"
			/>
			<button
				type="button"
				onClick={zoomIn}
				className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
			>
				<Icon src={Icons.searchZoomIn} width={25} height={25} />
			</button>
		</div>
		{pdfUrl && (
			<Link
				href={pdfUrl}
				target="_blank"
				rel="noopener noreferrer"
				className="rounded-full shadow-shadow2 bg-gray-5 border p-2"
			>
				<Icon src={Icons.externalLink} width={20} height={20} />
			</Link>
		)}
	</div>
);

export interface PdfZoomApi {
	scale: number;
	zoomIn: () => void;
	zoomOut: () => void;
	setScale: (scale: number) => void;
}

interface PdfViewerProps {
	pdfUrl: string;
	showControls?: boolean;
	showZoom?: boolean;
	onReachEnd?: () => void;
	extraControls?: React.ReactNode;
	rightControls?: React.ReactNode;
	hideZoomControls?: boolean;
	onZoomChange?: (zoom: PdfZoomApi) => void;
	/** Override the default vh-based viewport height (e.g. to fill a flex parent). */
	heightClassName?: string;
	/** Opt-in: notifies the loading / ready / error state of the document. */
	onStatusChange?: (status: PdfViewerStatus) => void;
	enableDragToPan?: boolean;
}

export type PdfViewerStatus = 'loading' | 'ready' | 'error';

declare global {
	interface Window {
		pdfjsLib: any;
	}
}

export default function PdfViewer({
	pdfUrl,
	onReachEnd,
	showControls = false,
	showZoom = false,
	extraControls,
	rightControls,
	hideZoomControls = false,
	onZoomChange,
	heightClassName,
	onStatusChange,
	enableDragToPan = false,
}: PdfViewerProps) {
	const { containerRef, checkScrollEnd, setHasReachedEnd } = useScrollEnd({
		offset: 60,
		onReachEnd,
	});
	const { containerRef: dragContainerRef, canPan, isPanning } = useDragToPan();
	const dragCursorClassName =
		enableDragToPan && canPan
			? isPanning
				? 'cursor-grabbing'
				: 'cursor-grab'
			: '';

	const [isRendered, setIsRendered] = useState(false);
	const [numPages, setNumPages] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [scale, setScale] = useState<number>(0.9); // Ahora es editable
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [pdfReady, setPdfReady] = useState(false);
	const pdfContainerRef = useRef<HTMLDivElement>(null);
	const pdfInstanceRef = useRef<any>(null);
	const scrollSetupDone = useRef(false);
	const renderAllPagesIdRef = useRef(0);
	const onStatusChangeRef = useRef(onStatusChange);
	onStatusChangeRef.current = onStatusChange;

	const status: PdfViewerStatus = error
		? 'error'
		: isLoading
		? 'loading'
		: 'ready';

	useEffect(() => {
		onStatusChangeRef.current?.(status);
	}, [status]);

	// Controles de zoom
	const zoomIn = useCallback(() => {
		setScale((prev) => Math.min(prev + 0.1, 2.5));
	}, []);

	const zoomOut = useCallback(() => {
		setScale((prev) => Math.max(prev - 0.1, 0.5));
	}, []);

	const handleZoomChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setScale(parseFloat(e.target.value));
		},
		[]
	);

	useEffect(() => {
		onZoomChange?.({ scale, zoomIn, zoomOut, setScale });
	}, [scale, zoomIn, zoomOut, onZoomChange]);

	const renderSinglePage = useCallback(
		async (pageNum: number) => {
			if (!pdfInstanceRef.current || !pdfContainerRef.current) {
				return;
			}

			try {
				const page = await pdfInstanceRef.current.getPage(pageNum);
				const container = pdfContainerRef.current;

				container.innerHTML = '';

				const canvas = document.createElement('canvas');
				canvas.style.display = 'block';
				canvas.style.margin = '10px auto 20px auto';
				canvas.style.boxShadow =
					'0 -2px 4px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.1)';
				canvas.style.borderRadius = '8px';

				container.appendChild(canvas);

				const context = canvas.getContext('2d');

				// Calcular scale para que quepa en el contenedor
				const baseViewport = page.getViewport({ scale: 1 });
				const containerWidth = container.clientWidth - 32; // padding
				const fitScale = containerWidth / baseViewport.width;
				// const finalScale = Math.min(scale, fitScale) * scale; // Aplica zoom sobre el fit

				const viewport = page.getViewport({
					scale: Math.max(fitScale * scale, 0.5),
				});

				const outputScale = window.devicePixelRatio || 1;
				canvas.width = Math.floor(viewport.width * outputScale);
				canvas.height = Math.floor(viewport.height * outputScale);
				canvas.style.width = `${Math.floor(viewport.width)}px`;
				canvas.style.height = `${Math.floor(viewport.height)}px`;

				if (outputScale !== 1 && context) {
					context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
				}

				await page.render({
					canvasContext: context,
					viewport,
				}).promise;
				setIsRendered(true);
			} catch (err) {
				console.error('Error rendering page:', err);
				setError('Error rendering the page');
			}
		},
		[scale]
	);

	const renderAllPages = useCallback(async () => {
		if (!pdfInstanceRef.current || !pdfContainerRef.current) return;

		const renderId = ++renderAllPagesIdRef.current;

		try {
			const container = pdfContainerRef.current;
			container.innerHTML = '';
			const containerWidth = container.clientWidth - 32;

			for (let i = 1; i <= pdfInstanceRef.current.numPages; i++) {
				const page = await pdfInstanceRef.current.getPage(i);
				if (renderId !== renderAllPagesIdRef.current) return;
				const canvas = document.createElement('canvas');

				canvas.style.display = 'block';
				canvas.style.margin = '20px auto 0px auto';
				canvas.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';

				container.appendChild(canvas);

				const context = canvas.getContext('2d');

				// Calcular scale para que quepa en el contenedor
				const baseViewport = page.getViewport({ scale: 1 });
				const fitScale = containerWidth / baseViewport.width;

				const viewport = page.getViewport({
					scale: Math.max(fitScale * scale, 0.5),
				});

				const outputScale = window.devicePixelRatio || 1;
				canvas.width = Math.floor(viewport.width * outputScale);
				canvas.height = Math.floor(viewport.height * outputScale);
				canvas.style.width = `${Math.floor(viewport.width)}px`;
				canvas.style.height = `${Math.floor(viewport.height)}px`;

				if (outputScale !== 1 && context) {
					context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
				}

				await page.render({
					canvasContext: context!,
					viewport,
				}).promise;
				if (renderId !== renderAllPagesIdRef.current) return;
			}
			setIsRendered(true);
		} catch (err) {
			console.error('Error rendering all pages:', err);
			setError('Error rendering the document');
		}
	}, [scale]);

	// Efecto para el scroll
	useEffect(() => {
		if (!isRendered || showControls || !containerRef.current) return;

		const container = containerRef.current;

		const safeCheckScrollEnd = () => {
			if (!container) return;
			checkScrollEnd();
		};

		const timeoutId = setTimeout(() => {
			container.addEventListener('scroll', safeCheckScrollEnd);
			safeCheckScrollEnd();
			scrollSetupDone.current = true;
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			container.removeEventListener('scroll', safeCheckScrollEnd);
			scrollSetupDone.current = false;
		};
	}, [isRendered, showControls, checkScrollEnd, containerRef]);

	const getProxyUrl = (originalUrl: string): string => {
		if (
			originalUrl.includes('/api/') ||
			!originalUrl.includes('s3.us-east-1.amazonaws.com')
		) {
			return originalUrl;
		}
		return `/api/proxy/s3-proxy?url=${decodeURIComponent(originalUrl)}`;
	};

	const loadPdfJs = useCallback(async () => {
		try {
			if (window.pdfjsLib) {
				window.pdfjsLib.GlobalWorkerOptions.workerSrc =
					'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
				return window.pdfjsLib;
			}

			return new Promise((resolve, reject) => {
				const script = document.createElement('script');
				script.src =
					'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
				script.onload = () => {
					window.pdfjsLib.GlobalWorkerOptions.workerSrc =
						'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
					resolve(window.pdfjsLib);
				};
				script.onerror = reject;
				document.head.appendChild(script);
			});
		} catch (err) {
			console.error('Error loading PDF.js:', err);
			setError('Error loading the PDF viewer');
			throw err;
		}
	}, []);

	const clearPdfContainer = useCallback(() => {
		if (pdfContainerRef.current) {
			pdfContainerRef.current.innerHTML = '';
		}
	}, []);

	const isImage = pdfUrl ? isImageUrl(pdfUrl) : false;
	const zoomControlsVisible =
		showZoom && (pdfReady || isImage) && !isLoading && !error;

	useEffect(() => {
		if (isImage) {
			setIsLoading(false);
			setError(null);
			setNumPages(1);
			setPageNumber(1);
			return;
		}

		const loadPdf = async () => {
			try {
				clearPdfContainer();
				setIsLoading(true);
				setError(null);
				scrollSetupDone.current = false;

				const pdfjs = await loadPdfJs();
				const proxyUrl = getProxyUrl(pdfUrl);
				const loadingTask = pdfjs.getDocument(proxyUrl);
				const pdf = await loadingTask.promise;

				pdfInstanceRef.current = pdf;
				setNumPages(pdf.numPages);
				setPdfReady(true);
			} catch (err) {
				console.error('Error loading PDF:', err);
				setError('Error loading the PDF document');
			} finally {
				setIsLoading(false);
			}
		};

		if (pdfUrl) {
			loadPdf();
		}

		return () => {
			scrollSetupDone.current = false;
			setPdfReady(false);
			setHasReachedEnd(false);
		};
	}, [pdfUrl, loadPdfJs, isImage]);

	// Re-renderizar cuando cambie el scale
	useEffect(() => {
		if (pdfReady && pdfContainerRef.current) {
			scrollSetupDone.current = false;
			setIsRendered(false);

			if (showControls) {
				renderSinglePage(pageNumber);
			} else {
				renderAllPages();
			}
		}
	}, [
		pdfReady,
		showControls,
		pageNumber,
		scale,
		renderSinglePage,
		renderAllPages,
	]);

	useEffect(() => {
		if (showControls && pdfReady) {
			renderSinglePage(pageNumber);
		}
	}, [pageNumber, showControls, pdfReady, renderSinglePage]);

	useEffect(() => {
		if (!pdfReady || isImage || !pdfContainerRef.current) return;

		const container = pdfContainerRef.current;
		let lastWidth = container.clientWidth;

		const resizeObserver = new ResizeObserver(() => {
			const newWidth = container.clientWidth;
			if (Math.abs(newWidth - lastWidth) > 1) {
				lastWidth = newWidth;
				if (showControls) {
					renderSinglePage(pageNumber);
				} else {
					renderAllPages();
				}
			}
		});

		resizeObserver.observe(container);
		return () => resizeObserver.disconnect();
	}, [
		pdfReady,
		isImage,
		showControls,
		pageNumber,
		renderSinglePage,
		renderAllPages,
	]);

	const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
	const goToNextPage = () =>
		setPageNumber((prev) => Math.min(prev + 1, numPages));

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className="flex justify-center items-center h-full w-full">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-main mx-auto mb-2"></div>
						<Typography type="regular-14" className="text-gray-35">
							Loading PDF...
						</Typography>
					</div>
				</div>
			);
		}

		if (error) {
			return (
				<div className="flex flex-col justify-center items-center h-full w-full p-4 bg-red-50 rounded-2xl">
					<p className="text-red-700 text-center">{error}</p>
					<div className="text-center mt-2">
						<Link
							href={pdfUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-600 underline"
						>
							Open PDF in new tab
						</Link>
					</div>
				</div>
			);
		}

		return null;
	};

	return (
		<>
			<style jsx>{`
				input[type='range']::-webkit-slider-thumb {
					-webkit-appearance: none;
					appearance: none;
					width: 12px;
					height: 12px;
					background: #8039df;
					border-color: #8039df;
					border-radius: 50%;
					cursor: pointer;
				}
				input[type='range']::-moz-range-thumb {
					width: 16px;
					height: 16px;
					background: #8039df;
					border-radius: 50%;
					border: none;
					cursor: pointer;
				}
			`}</style>
			<div
				className={`border border-grey-100 rounded-2xl bg-white relative overflow-hidden flex flex-col ${
					heightClassName ??
					(showControls
						? 'h-[495px] md:h-[65vh] xl:h-[67.5vh] 3xl:h-[69vh] 4xl:h-[68.5vh]'
						: 'h-[495px] md:h-[73vh] 2xl:h-[73vh]')
				}`}
			>
				{showControls && (
					<div className="flex flex-col md:flex-row items-center justify-between gap-4 lg:py-6 px-4 bg-white rounded-t-2xl border-b border-grey-100 shrink-0">
						{extraControls ? extraControls : <div />}
						<div className="flex items-center gap-4">
							<div aria-live="polite">
								<Typography type="regular-14" className="text-gray-35">
									Page {pageNumber} of {numPages}
								</Typography>
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									aria-label="Previous page"
									onClick={goToPrevPage}
									disabled={pageNumber <= 1}
									className="disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<ChevronLeft />
								</button>
								<button
									type="button"
									aria-label="Next page"
									onClick={goToNextPage}
									disabled={pageNumber >= numPages}
									className="disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<ChevronRight />
								</button>
							</div>
						</div>
						{rightControls ? rightControls : <div />}
					</div>
				)}

				<div className="relative flex-1 min-h-0">
					{/* Placeholder para carga y errores */}
					{(isLoading || error) && (
						<div className="absolute inset-0 z-20 bg-white rounded-2xl">
							{renderContent()}
						</div>
					)}

					{/* Contenedor del PDF / Imagen */}
					{isImage ? (
						<div className="w-full h-full px-4 pb-4 overflow-auto">
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img
								src={pdfUrl}
								alt="Document"
								className="rounded-lg"
								style={{
									display: 'block',
									width: `${scale * 100}%`,
									maxWidth: 'none',
									margin: '10px auto 20px auto',
									boxShadow:
										'0 -2px 4px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.1)',
									transition: 'width 0.15s ease',
								}}
							/>
						</div>
					) : (
						<div
							ref={(node) => {
								(
									containerRef as React.MutableRefObject<HTMLDivElement | null>
								).current = node;
								(
									pdfContainerRef as React.MutableRefObject<HTMLDivElement | null>
								).current = node;
								if (enableDragToPan) {
									(
										dragContainerRef as React.MutableRefObject<HTMLDivElement | null>
									).current = node;
								}
							}}
							className={`w-full h-full px-4 pb-4 overflow-y-auto overflow-x-auto ${dragCursorClassName} ${
								isLoading || error ? 'invisible' : 'visible'
							}`}
						/>
					)}

					{/* Controles de zoom */}
					{!hideZoomControls && zoomControlsVisible && (
						<ZoomControls
							zoomOut={zoomOut}
							zoomIn={zoomIn}
							scale={scale}
							handleZoomChange={handleZoomChange}
							pdfUrl={pdfUrl}
						/>
					)}
				</div>
			</div>
		</>
	);
}
